import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { MonitoringService } from '../services/monitoring/monitoring.service';
import { IMonitoringLogger } from '../interface/monitoring-logger';
import { ActionEnum } from '../types/enums/ActionEnum.enum';
import { channel } from 'version';
import { AppContext } from '../services/context/app.context.service';
import { SidenavService } from '../services/side-nav/side-nav.service';
import { AuthService } from '@auth0/auth0-angular';
import { switchMap, take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class MonitoringResolver implements Resolve<void> {
  constructor(
    private readonly monitoringService: MonitoringService,
    private readonly appCtxSvc: AppContext,
    private readonly sidenavService: SidenavService,
    private readonly authSvc: AuthService
  ) {
    this.sidenavService.setMonitoringCallBack(component => {
      this.createAndSendMonitoringData('SIDENAV', component);
    });
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const pathSegments = state.url.split('/').filter(segment => segment && segment !== ':id' && segment !== ':country');
    const currentView = pathSegments[pathSegments.length - 1] || 'unknown';

    this.createAndSendMonitoringData(state.url, currentView);
    return of(null);
  }

  private createAndSendMonitoringData(field: string, view: string): void {
    const existingTraceId = this.appCtxSvc.getTraceSessionId();
    const existingUserEmail = this.appCtxSvc.getUserAgentEmail();

    // Si no hay traceId válido, generamos uno temporal
    const traceId = existingTraceId && existingTraceId.trim() !== '' ? existingTraceId : uuidv4();

    // Si generamos un nuevo traceId temporal, lo guardamos para consistencia
    if (traceId !== existingTraceId) {
      this.appCtxSvc.setTraceSessionId(traceId);
    }

    if (existingUserEmail && existingUserEmail.trim() !== '') {
      // Si ya tenemos email válido, enviamos directamente
      const data = this.createMonitoringData(field, view, existingUserEmail, traceId);
      this.monitoringService.setMonitoring(data).subscribe();
    } else {
      // Si falta el email del usuario, lo obtenemos de Auth0
      this.authSvc.user$
        .pipe(
          take(1),
          switchMap(user => {
            const userEmail = user?.email || '';

            // Actualizamos el email si lo obtuvimos
            if (userEmail && userEmail.trim() !== '') {
              this.appCtxSvc.setUserAgentEmail(userEmail);
            }

            const data = this.createMonitoringData(field, view, userEmail, traceId);
            return this.monitoringService.setMonitoring(data);
          })
        )
        .subscribe();
    }
  }

  private createMonitoringData(field: string, view: string, userEmail: string, traceId: string): IMonitoringLogger {
    return {
      ip: '',
      user_agent: navigator.userAgent,
      time: new Date().toISOString(),
      user: userEmail,
      action: ActionEnum.READ,
      field: field,
      view: view || 'unknown',
      channel: channel + '-' + this.appCtxSvc.getUserType(),
      traceability_id: traceId,
      request: {
        data: '',
      },
      response: {
        data: '',
      },
    };
  }
}
